import React from "react";
import MuiAutocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { HiLockClosed } from "react-icons/hi2";

const BADGE_COLORS = {
  Z: "bg-blue-100 text-blue-700",
  B: "bg-purple-100 text-purple-700",
  I: "bg-green-100 text-green-700",
};

const renderBadge = (badge, extraClass = "") => {
  const label = typeof badge === "string" ? badge : badge?.label;
  if (!label) return null;
  const className = typeof badge === "string"
    ? BADGE_COLORS[badge] || "bg-gray-100 text-gray-600"
    : badge.className || "bg-gray-100 text-gray-600";

  return (
    <span className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-bold leading-none whitespace-nowrap ${className} ${extraClass}`}>
      {label}
    </span>
  );
};

const getOptionBadges = (option) => [
  ...(option?.badge ? [option.badge] : []),
  ...(Array.isArray(option?.badges) ? option.badges : []),
];

// Wrapper attorno a MUI Autocomplete con la stessa API del componente Select:
// - options: [{ id, name, badge?, badges? }]  — badge è opzionale (es. 'Z'/'B'/'I')
// - value: id selezionato (stringa/numero)
// - onChange: chiamato con { target: { value: id } }
export const Autocomplete = ({
  label,
  placeholder,
  value,
  onChange,
  options = [],
  disabled,
  name,
  badgePosition = "left",
}) => {
  const selected = options.find((o) => String(o.id) === String(value)) || null;
  const renderBadges = (option, className = "") => {
    const badges = getOptionBadges(option);
    if (badges.length === 0) return null;

    return (
      <span className={`flex shrink-0 items-center gap-1.5 ${className}`}>
        {badges.map((badge, index) => (
          <React.Fragment key={`${option?.id || "selected"}-badge-${index}`}>
            {renderBadge(badge)}
          </React.Fragment>
        ))}
      </span>
    );
  };

  return (
    <div className="mb-2">
      {(label || disabled) && (
        <label className="text-gray-500 text-xs font-medium uppercase tracking-wider px-1">
          {label} {disabled ? <HiLockClosed className="inline" /> : ""}
        </label>
      )}
      <MuiAutocomplete
        options={options}
        getOptionLabel={(o) => o.name || ""}
        isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
        value={selected}
        disabled={disabled}
        onChange={(_, newValue) => {
          onChange({ target: { name, value: newValue?.id ?? "" } });
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <span className="flex items-center gap-2 w-full">
              {badgePosition === "left" && renderBadges(option)}
              <span className="truncate">{option.name}</span>
              {badgePosition === "right" && renderBadges(option, "ml-auto")}
            </span>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: badgePosition !== "right" && getOptionBadges(selected).length > 0 ? (
                <>
                  {renderBadges(selected, "ml-1 mr-1")}
                  {params.InputProps.startAdornment}
                </>
              ) : params.InputProps.startAdornment,
              endAdornment: badgePosition === "right" && getOptionBadges(selected).length > 0 ? (
                <>
                  {renderBadges(selected, "mr-1")}
                  {params.InputProps.endAdornment}
                </>
              ) : params.InputProps.endAdornment,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "0.375rem",
                backgroundColor: disabled ? "#f3f4f6" : "white",
                fontSize: "0.875rem",
              },
            }}
          />
        )}
        noOptionsText="Nessun risultato"
        clearText="Svuota"
        openText="Apri"
        closeText="Chiudi"
      />
    </div>
  );
};
